import { useCallback } from "react";
import {
  keepPreviousData,
  useQueries,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  getConfigureOptions,
  type ConfigureOptionsResponse,
} from "../api/configure-options";
import type { RegionCode } from "../api/filter";
import { toCalculationRateLimitError } from "../utils/calculation-request-control";
import { autoFeedSupport } from "../utils/configure-auto-select";

export interface UseConfigureOptionsArgs {
  productId: string | null;
  region: RegionCode;
  /**
   * Apps checked for THIS machine (Q1) — drives tooling lookups + auto-select.
   * Snapshotted at modal-open time per Q7; stable for the modal's lifetime.
   */
  applicationIds: string[];
  /** Compare-card MSPe toggle (D9). */
  mspAutoFeedEnabled?: boolean;
  /** Modal-open flag — short-circuits the query when the modal is closed. */
  enabled: boolean;
}

/**
 * Fetches `GET /v1/products/:productId/configure-options`. The query key
 * includes `applicationIds` so refresh-on-assignment-change works (POC
 * `refreshOpenConfigSelectionsAfterAssignmentChange` parity). Region is
 * snapshotted by the caller; this hook treats it as a normal cache key.
 *
 * Stale time = 30s — admin PUTs to cost-models bump version, but the
 * configure surface reads catalog tables that don't share that cache key.
 * The user closing + reopening the modal hard-resets via `enabled=false`.
 */
export function useConfigureOptions(args: UseConfigureOptionsArgs) {
  const { productId, region, applicationIds, mspAutoFeedEnabled, enabled } = args;
  return useQuery<ConfigureOptionsResponse>({
    queryKey: [
      "configure-options",
      productId,
      region,
      [...applicationIds].sort().join(","),
      mspAutoFeedEnabled ?? null,
    ],
    queryFn: ({ signal }) =>
      getConfigureOptions(
        productId as string,
        {
          region,
          applicationIds,
          mspAutoFeedEnabled,
        },
        signal,
      ),
    enabled: enabled && !!productId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: (failureCount, error) =>
      !toCalculationRateLimitError(error) && failureCount < 1,
  });
}

/**
 * MICS-181: machines whose regional catalog offers an auto-feed upgrade, so the
 * comparison card shows the Auto-Feed toggle and the cost-based pre-selection
 * runs. Absent while loading; false when the lookup fails. Same key as the modal
 * query with no applications, so the request is shared.
 */
export function useAutoFeedToggles(
  productIds: string[],
  region: RegionCode,
): Record<string, boolean> {
  const combine = useCallback(
    (results: UseQueryResult<ConfigureOptionsResponse>[]) =>
      Object.fromEntries(
        productIds.flatMap((productId, i) => {
          const result = results[i];
          if (!result || result.isPending) return [];
          const data = result.data;
          return [[productId, !!data?.available && autoFeedSupport(data).upgradeOffered]];
        }),
      ),
    [productIds],
  );
  return useQueries({
    queries: productIds.map((productId) => ({
      queryKey: ["configure-options", productId, region, "", null],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        getConfigureOptions(productId, { region }, signal),
      staleTime: 30_000,
      retry: (failureCount: number, error: unknown) =>
        !toCalculationRateLimitError(error) && failureCount < 1,
    })),
    combine,
  });
}
